import { create } from "zustand";
import { useShallow } from "zustand/react/shallow";

const defaultContractState = {
  status: "empty",
  progress: 0,
  progressMessage: "",
  error: null,
};

const DEFAULT_CONTRACT_PANEL_WIDTH = 320;
const MIN_CONTRACT_PANEL_WIDTH = 240;
const MAX_CONTRACT_PANEL_WIDTH = 600;

export const useDraftFlowStore = create((set) => ({
  selectedProjectId: "",
  activeSessionId: null,
  intentData: null,
  contractState: defaultContractState,
  contractId: null,
  selectedContractId: null,
  pendingAssistant: false,
  pendingMessageId: null,
  sandboxProgressByJobId: {},
  agentProgress: null,
  editingSessionId: null,
  editingTitle: "",
  isProjectPopoverOpen: false,
  showSidebar: true,
  showContractPanel: false,
  contractPanelWidth: DEFAULT_CONTRACT_PANEL_WIDTH,
  grant: null,
  grantLoading: false,
  summaryMeta: null,
  permissionPickerOpen: false,
  setSelectedProjectId: (selectedProjectId) => set({ selectedProjectId }),
  setActiveSessionId: (activeSessionId) => set({ activeSessionId }),
  setIntentData: (intentData) => set({ intentData }),
  setContractState: (updater) =>
    set((state) => ({
      contractState:
        typeof updater === "function" ? updater(state.contractState) : updater,
    })),
  setContractId: (contractId) => set({ contractId }),
  setSelectedContractId: (selectedContractId) => set({ selectedContractId }),
  setPendingAssistant: (pendingAssistant) => set({
    pendingAssistant,
    ...(!pendingAssistant ? { agentProgress: null } : {}),
  }),
  setPendingMessageId: (pendingMessageId) => set({ pendingMessageId }),
  setSandboxProgress: (event, sessionId) => set((state) => {
    if (String(event?.session_id) !== String(sessionId || "") || !event?.job_id) return state;
    return { sandboxProgressByJobId: { ...state.sandboxProgressByJobId, [event.job_id]: event } };
  }),
  clearSandboxProgress: (jobId) => set((state) => {
    const next = { ...state.sandboxProgressByJobId };
    delete next[jobId];
    return { sandboxProgressByJobId: next };
  }),
  setAgentProgress: (event, sessionId) => set((state) => {
    if (String(event?.session_id) !== String(sessionId || "") ||
        !event?.message || !state.pendingAssistant ||
        !event?.client_message_id || event.client_message_id !== state.pendingMessageId) return state;
    return { agentProgress: event };
  }),
  clearAgentProgress: () => set({ agentProgress: null }),
  setEditingSessionId: (editingSessionId) => set({ editingSessionId }),
  setEditingTitle: (editingTitle) => set({ editingTitle }),
  setIsProjectPopoverOpen: (isProjectPopoverOpen) => set({ isProjectPopoverOpen }),
  setShowSidebar: (updater) =>
    set((state) => ({
      showSidebar:
        typeof updater === "function" ? updater(state.showSidebar) : updater,
    })),
  setShowContractPanel: (updater) =>
    set((state) => ({
      showContractPanel:
        typeof updater === "function" ? updater(state.showContractPanel) : updater,
    })),
  setContractPanelWidth: (width) =>
    set((state) => ({
      contractPanelWidth: Math.max(
        MIN_CONTRACT_PANEL_WIDTH,
        Math.min(MAX_CONTRACT_PANEL_WIDTH, typeof width === "function" ? width(state.contractPanelWidth) : width),
      ),
    })),
  setGrant: (grant) => set({ grant }),
  setGrantLoading: (grantLoading) => set({ grantLoading }),
  setSummaryMeta: (summaryMeta) => set({ summaryMeta }),
  setPermissionPickerOpen: (permissionPickerOpen) => set({ permissionPickerOpen }),
  resetSessionState: () =>
    set({
      intentData: null,
      contractState: defaultContractState,
      contractId: null,
      selectedContractId: null,
      pendingAssistant: false,
      pendingMessageId: null,
      sandboxProgressByJobId: {},
      agentProgress: null,
      editingSessionId: null,
      editingTitle: "",
      showContractPanel: false,
      contractPanelWidth: DEFAULT_CONTRACT_PANEL_WIDTH,
      grant: null,
      grantLoading: false,
      summaryMeta: null,
      permissionPickerOpen: false,
    }),
  resetProjectState: () =>
    set({
      activeSessionId: null,
      intentData: null,
      contractState: defaultContractState,
      contractId: null,
      selectedContractId: null,
      pendingAssistant: false,
      pendingMessageId: null,
      sandboxProgressByJobId: {},
      agentProgress: null,
      editingSessionId: null,
      editingTitle: "",
      showContractPanel: false,
      contractPanelWidth: DEFAULT_CONTRACT_PANEL_WIDTH,
      grant: null,
      grantLoading: false,
      summaryMeta: null,
      permissionPickerOpen: false,
    }),
}));

export { useShallow };
